import React from 'react';
import { FormField } from '../../../components/FormField';
import { FormRow, FormRowThree } from '../../../components/FormRow';
import { apartmentTypes, houseTypes, conditions, views } from '../../../constants/propertyOptions';
import { formatArea, formatAreaWithDecimals } from '../../../../../../utils/formatters';

interface GeneralInfoProps {
  formData: any;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  isHouse: boolean;
}

export const GeneralInfo: React.FC<GeneralInfoProps> = ({
  formData,
  handleInputChange,
  isHouse,
}) => {
  // Gestionnaire pour les champs de surface (autorise les décimales)
  const handleAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Autoriser les chiffres ET le point/virgule pour les décimales
    const numericValue = value.replace(/[^\d.,]/g, '').replace(',', '.');
    
    handleInputChange({
      target: {
        name,
        value: numericValue,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        1. Informations générales
      </h3>

      <FormRow>
        {isHouse ? (
          <FormField label="Type de maison">
            <select
              name="houseType"
              value={formData.houseType || ''}
              onChange={handleInputChange}
              className="w-full"
            >
              <option value="">Sélectionner</option>
              {houseTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </FormField>
        ) : (
          <FormField label="Type d'appartement">
            <select
              name="apartmentType"
              value={formData.apartmentType || ''}
              onChange={handleInputChange}
              className="w-full"
            >
              <option value="">Sélectionner</option>
              {apartmentTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </FormField>
        )}

        <FormField label="État général">
          <select
            name="condition"
            value={formData.condition || ''}
            onChange={handleInputChange}
            className="w-full"
          >
            <option value="">Sélectionner</option>
            {conditions.map(condition => (
              <option key={condition} value={condition}>{condition}</option>
            ))}
          </select>
        </FormField>
      </FormRow>

      <FormRowThree>
        <FormField label="Surface habitable (m²)">
          <input
            type="text"
            name="livingArea"
            value={formatAreaWithDecimals(formData.livingArea)}
            onChange={handleAreaChange}
            placeholder="0"
          />
        </FormField>

        <FormField label="Surface utile (m²)">
          <input
            type="text"
            name="usableArea"
            value={formatAreaWithDecimals(formData.usableArea)}
            onChange={handleAreaChange}
            placeholder="0"
          />
        </FormField>

        <FormField label="Surface salon/séjour (m²)">
          <input
            type="text"
            name="livingRoomArea"
            value={formatArea(formData.livingRoomArea)}
            onChange={handleAreaChange}
            placeholder="0"
          />
        </FormField>
      </FormRowThree>

      {isHouse && (
        <FormRow>
          <FormField label="Surface du terrain (m²)">
            <input
              type="text"
              name="landArea"
              value={formatArea(formData.landArea)}
              onChange={handleAreaChange}
              placeholder="0"
            />
          </FormField>

          <FormField label="Nombre de niveaux">
            <input
              type="number"
              name="floorCount"
              value={formData.floorCount || ''}
              onChange={handleInputChange}
              min="1"
            />
          </FormField>
        </FormRow>
      )}

      {!isHouse && (
        <FormRow>
          <FormField label="Étage">
            <input
              type="number"
              name="floor"
              value={formData.floor || ''}
              onChange={handleInputChange}
              min="0"
              placeholder="Étage actuel"
            />
          </FormField>
          <FormField label="Nombre total d'étages">
            <input
              type="number"
              name="totalFloors"
              value={formData.totalFloors || ''}
              onChange={handleInputChange}
              min="1"
              placeholder="Total d'étages"
            />
          </FormField>
        </FormRow>
      )}

      <FormRow>
        <FormField label="Année de construction">
          <input
            type="number"
            name="constructionYear"
            value={formData.constructionYear || ''}
            onChange={handleInputChange}
            min="1800"
            max={new Date().getFullYear()}
            placeholder="AAAA"
          />
        </FormField>

        <FormField label="Vue">
          <select
            name="view"
            value={formData.view || ''}
            onChange={handleInputChange}
            className="w-full"
          >
            <option value="">Sélectionner</option>
            {views.map(view => (
              <option key={view} value={view}>{view}</option>
            ))}
          </select>
        </FormField>
      </FormRow>
    </div>
  );
};